import { Carousel, Form, Input, InputRef } from 'antd';
import { CarouselRef } from 'antd/es/carousel';
import Icon from 'assets/Icons';
import Checkbox from 'components/Checkbox';
import Modal from 'components/Modal';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import PrintBodyCouponOnly from 'features/payment/pages/PrintPage/components/PrintBodyCouponOnly';
import userApis from 'features/user/services/apis';
import { delay, get } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { IResponseDataBody } from 'services/response';
import { ICouponPrintDiscount } from 'services/shop/types/coupon';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import styled from 'styled-components';
import toast from 'utils/toast';
type Props = {};
type HomeCouponMarketingModalRef = {
  open: (data?: IStaffItemData) => void;
  close: () => void;
};
export const useHomeCouponMarketingModal = () => useRef<HomeCouponMarketingModalRef>(null);
const HomeCouponMarketingModal = forwardRef<HomeCouponMarketingModalRef, Props>((_props, ref) => {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [staff, setStaff] = useState<IStaffItemData | null>(null);
  const [coupons, setCoupons] = useState<ICouponPrintDiscount[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const carouselRef = useRef<CarouselRef>(null);
  const [amount, setAmount] = useState(1);
  const printRef = useRef<HTMLDivElement>(null);
  const printIndex = useRef(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const setLoadingPage = useSetLoadingPage();
  const couponPrintDataRef = useRef<ICouponPrintDiscount[]>([]);
  const [currentCouponPrintData, setCurrentCouponPrintData] = useState<ICouponPrintDiscount | null>(null);
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Reset carousel to the first slide once it is mounted (modal becomes visible).
  useEffect(() => {
    if (open) carouselRef.current?.goTo(0, true);
  }, [open]);

  const onOpen: HomeCouponMarketingModalRef['open'] = async (_staff) => {
    setStaff(_staff || null);
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<ICouponPrintDiscount[]> = await userApis.getCouponMarketing();
      const data = res.data.data;
      if (data) {
        const result = data.map(o => ({
          ...o,
          itemDesc: o.itemDesc || o.description?.split('\r\n') || [],
        }));
        setCoupons(result);
        setSelectedIds(result[0]?.id ? [result[0].id] : []);
        setAmount(1);
        setTimeout(() => inputRef.current?.focus(), 100);
        setOpen(true);
        setIsPrinting(false);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Get coupon marketing failed');
    } finally {
      setLoadingPage(false);
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  const oPrintMultiple = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      if (printIndex.current < couponPrintDataRef.current.length - 1) {
        printIndex.current++;
        setCurrentCouponPrintData(couponPrintDataRef.current?.[printIndex.current]);
        delay(oPrintMultiple, 200);
      } else {
        setIsPrinting(false);
        setLoadingPage(false);
        onClose();
      }
    },
  });

  const onSubmit = async () => {
    printIndex.current = 0;
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<ICouponPrintDiscount[]> = await userApis.printCouponMarketing({
        staffId: staff?.id || '',
        couponIds: selectedIds,
        totalPrint: amount,
      });
      const data = res.data.data;
      if (data && data.length) {
        couponPrintDataRef.current = data;
        setCurrentCouponPrintData(data[0]);
        delay(oPrintMultiple, 500);
      } else {
        setLoadingPage(false);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Print coupon marketing failed');
      setLoadingPage(false);
    } finally {
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  return (
    <Modal
      visible={open}
      onClose={onClose}
      width={'auto'}
      v2
      modalTitle='print coupon'
      noneBodyStyle={true}
      onSubmit={onSubmit}
      disableOk={selectedIds.length === 0 || amount <= 0}
      className={isPrinting ? 'display-none-modal' : ''}
      okTitle='OK'

    >
      <FormStyled layout='vertical'>
        <Container>
          <CouponViewStyled>
            <div className="carousel-wrap">
              <Carousel
                ref={carouselRef}
                dots={coupons.length > 1}
                infinite={false}
              >
                {coupons.map((coupon) => (
                  <div
                    key={coupon.id}
                    className={`coupon-slide${selectedIds.includes(coupon.id) ? ' active' : ''}`}
                    onClick={() => toggleSelect(coupon.id)}
                  >
                    <div className="coupon-select">
                      <Checkbox
                        value={selectedIds.includes(coupon.id)}
                        customSize={48}
                      />
                    </div>
                    <PrintBodyCouponOnly coupons={[coupon]} />
                  </div>
                ))}
              </Carousel>
              {coupons.length > 1 && (
                <>
                  <PrevButton onClick={() => carouselRef.current?.prev()}>
                    <Icon type="nextCarousel" />
                  </PrevButton>
                  <NextButton onClick={() => carouselRef.current?.next()}>
                    <Icon type="nextCarousel" />
                  </NextButton>
                </>
              )}
            </div>
          </CouponViewStyled>
          {currentCouponPrintData && <div style={{ display: 'none' }}>
            <PrintBodyCouponOnly key={currentCouponPrintData?.couponCode} billRef={printRef} coupons={currentCouponPrintData ? [currentCouponPrintData] : []} />
          </div>}
          <GiftCardNumberContainer>
            <Text className='modal-label' mb={0.5}>
              Amount
            </Text>
            <div className="input-selector-container">
              <>
                <Input
                  ref={inputRef}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className='gift-card-number-input' size='small'
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </>
            </div>
            <>
              <NumberPadV2
                type={'NUMBER'}
                controlRef={inputRef}
                value={amount?.toString() || ''}
                onChange={(e) => setAmount(Number(e))}
              />
            </>
          </GiftCardNumberContainer>
        </Container>
      </FormStyled>
    </Modal>
  );
});

HomeCouponMarketingModal.displayName = 'HomeCouponMarketingModal';
export default HomeCouponMarketingModal;

const FormStyled = styled(Form)`
background: #F5F5F5;
margin-bottom: 6px;
`;

const Container = styled.div`
  background: #F5F5F5;
  display: flex;
`;

const CouponViewStyled = styled.div`
display: flex;
padding: 16px 24px;
justify-content: center;
align-items: flex-start;
align-self: stretch;
background: #F5F5F5;
.carousel-wrap {
  position: relative;
  width: 27rem;
}
.ant-carousel .slick-slide {
  text-align: center;
}
.coupon-slide {
  display: flex !important;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
}
.coupon-slide.active {
  background: #eee;
}
.coupon-select {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}
.PrintBodyCouponOnlyContainer {
  padding: 0;
  align-self: stretch;
  justify-content: center;
  align-items: center;
  .TicketBox {
    padding: 16px 8px;
  }
}
`;

const NextButton = styled.div`
  position: absolute;
  top: 50%;
  right: -28px;
  transform: translateY(-50%);
  z-index: 1;
  cursor: pointer;
`;

const PrevButton = styled.div`
  position: absolute;
  top: 50%;
  left: -28px;
  transform: translateY(-50%);
  z-index: 1;
  cursor: pointer;
  > svg {
    rotate: 180deg;
  }
`;

const GiftCardNumberContainer = styled.div`
display: flex;
flex-direction: column;  
background: #fff;
padding-top: 16px;
align-self: stretch;
.modal-label {
  color: #64748B;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.input-selector-container {
  display: flex;
  align-items: center;
  position: relative;
  height: 64px;
  margin: 0 40px;
}
.gift-card-number-input {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 40px;
  height: unset;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;
  box-shadow: none;
  &.fs-big {
    font-size: 55px;
  }
  &.fs-medium {
    font-size: 45px;
  }
}

`;
